import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { FeatureFlag } from '@app/enums';
import { SlotAssignment } from '@app/modules/documents-v2/classes';
import { SigningTemplateSlotNames } from '@app/modules/documents-v2/enums/signing-template-slot-names.enum';
import { AssignableSlot } from '@app/modules/documents-v2/interfaces/assignable-slot.interface';
import { EmployeeTemplateCustomFieldsQueryService } from '@app/modules/documents-v2/services/employee-template-custom-fields-query.service';
import { SlotAssignmentOrder } from '@app/modules/documents-v2/types/slot-assignment-order.type';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { TemplateSlotNamesDisplayOptions } from '@app/types/translatables/template-slot-names-display.options';
import { CustomFieldsTemplateQueryService } from '@documents-v2/services/custom-fields-template-query.service';
import { QueryFetcher } from '@models/core/query-fetcher.model';
import { SigningTemplateSlot } from '@models/documents/signing-template-slot.model';
import { SigningTemplate } from '@models/documents/signing-template.model';
import { Employee } from '@models/employee/employee.model';

@Component({
    selector: 'app-form-multi-signer-assignment',
    templateUrl: './multi-signer-assignment.template.html',
    styleUrls: ['./multi-signer-assignment.style.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: forwardRef(() => NgForm) }],
})
export class MultiSignerAssignmentForm implements OnInit {
    @Input() set template(data: SigningTemplate) {
        this._template = data;

        if (this._template?.slots.length) {
            this.setup();

            this.onSlotChange.emit(this.slotAssignments);
        }
    }

    get template(): SigningTemplate {
        return this._template;
    }

    /**
     * isSettingDefaultAssignment is used to determine
     * if the form is being used to set the default assignments,
     * or just the assignments for the current instance.
     */
    @Input() isSettingDefaultAssignment = false;

    @Input() set assigningOrder(value: boolean) {
        // "Assign signing order" off/on.
        this._assigningOrder = value;

        if (this._assigningOrder) {
            // When toggled on, init a default order
            this.slotAssignments.forEach((s, i) => {
                if (!s.slot.order) {
                    s.order = (i + 1) as SlotAssignmentOrder;
                }
            });

            // Re-index array based on order props
            this.slotAssignments.sort((a: SlotAssignment, b: SlotAssignment) =>
                this.assigningOrder ? a.order - b.order : 0
            );
        } else {
            // When toggled off, remove order
            this.slotAssignments.forEach((s) => (s.order = null as SlotAssignmentOrder));
        }

        this.onSlotChange.emit(this.slotAssignments);
    }

    @Input() allowNone = false;

    get assigningOrder(): boolean {
        return this._assigningOrder;
    }

    @Input() isRequired = true;

    @Output() onSlotChange = new EventEmitter<SlotAssignment[]>();

    slotDisplayOptions = TemplateSlotNamesDisplayOptions;
    slotAssignments: SlotAssignment[] = [];
    animateIndices: boolean[] = [];
    employeeAutocompleteQuery: QueryFetcher = Employee.param('company', this.auth.company.id).whereIn('status', [
        'active',
    ]);
    allowMultiSignDocuments = false;

    private _template: SigningTemplate = new SigningTemplate();
    private _assigningOrder = false;

    constructor(
        protected auth: AuthService,
        private featureService: FeatureService,
        private http: HttpClient,
        private queryService: EmployeeTemplateCustomFieldsQueryService,
        private notifyService: NotifyService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.setFeatureFlag();
        this.setEmployeeAutocompleteQuery();
    }

    /**
     * When we select an employee, query for the custom field details and populate the hint text.
     */
    async onModelChange(employee: Employee, assignment: SlotAssignment): Promise<void> {
        assignment.hint = '';
        this.onSlotChange.emit(this.slotAssignments);
        if (!employee) {
            return;
        }

        try {
            const customFields = await this.queryService.query(this.template, [employee], assignment.slot);
            const missing = customFields[employee.id].filter((values: AssignableSlot) => !values.hasValue);
            if (missing.length) {
                assignment.hint = 'Missing the following: ' + missing.map((val) => val.name).join(', ');
                this.onSlotChange.emit(this.slotAssignments);
            }
        } catch (e) {
            this.notifyService.error(e);
        }
    }

    /**
     * Builder to create instances of CustomFieldsTemplateQueryService, each one is stateful based on the template and slot and used
     * to find out what (if any) custom fields an employee has/has not got from a given template.
     */
    subTitleFunctionBuilder(slot: SigningTemplateSlot): CustomFieldsTemplateQueryService {
        return new CustomFieldsTemplateQueryService(
            new EmployeeTemplateCustomFieldsQueryService(this.http),
            this.template,
            slot
        );
    }

    reset(): void {
        this.setup();
        setTimeout(() => {
            $('.page.dimmer.visible.active :not(.permanently-assigned) > .ui.dropdown').dropdown('clear');
        });
    }

    setup(): void {
        if (!this.template) {
            return;
        }

        this.slotAssignments = this.template.slots
            .map((slot: SigningTemplateSlot) => new SlotAssignment(slot))
            .filter((s) => s.slot.name !== SigningTemplateSlotNames.Candidate);

        // If there is an assigning order or an order already exists from before make sure to order it properly
        if (this.assigningOrder || this.template?.slots.some((s: SigningTemplateSlot) => s.order)) {
            // Re-index array based on order props
            this.slotAssignments.sort((a: SlotAssignment, b: SlotAssignment) => a.order - b.order);
        }

        if (this.isSettingDefaultAssignment) {
            this.slotAssignments.forEach((s) => (s.permanentSlotAssignment = true));
        }

        this.animateIndices = this.template.slots.map(() => false);
    }

    increaseOrder(slot: SlotAssignment): void {
        if (!slot.order) {
            return;
        }

        if (slot.order === this.slotAssignments.length) {
            return;
        }

        const newOrder = (slot.order + 1) as SlotAssignmentOrder;

        const slotToSwitchWith = this.slotAssignments.find((s) => s.order === newOrder);

        // If for some reason we don't find a slot to switch with, return early.
        if (!slotToSwitchWith || !slotToSwitchWith.order) {
            return;
        }

        slotToSwitchWith.order = (slotToSwitchWith.order - 1) as SlotAssignmentOrder;

        slot.order = newOrder;

        const slotIndex = this.slotAssignments.indexOf(slot);
        const slotToSwitchWithIndex = this.slotAssignments.indexOf(slotToSwitchWith);

        if (typeof slotIndex === 'number' && typeof slotToSwitchWithIndex === 'number') {
            this.slotAssignments[slotIndex] = slotToSwitchWith;
            this.slotAssignments[slotToSwitchWithIndex] = slot;
        }

        this.onSlotChange.emit(this.slotAssignments);

        this.setSlotAnimationIndex(newOrder - 1);
    }

    decreaseOrder(slot: SlotAssignment): void {
        if (!slot.order) {
            return;
        }

        // If slot is already at the lowest order, return early.
        if (slot.order === 1) {
            return;
        }

        const newOrder = (slot.order - 1) as SlotAssignmentOrder;

        const slotToSwitchWith = this.slotAssignments.find((s) => s.order === newOrder);

        // If for some reason we don't find a slot to switch with, return early.
        if (!slotToSwitchWith || !slotToSwitchWith.order) {
            return;
        }

        slotToSwitchWith.order = (slotToSwitchWith.order + 1) as SlotAssignmentOrder;

        slot.order = newOrder;

        const slotIndex = this.slotAssignments.indexOf(slot);
        const slotToSwitchWithIndex = this.slotAssignments.indexOf(slotToSwitchWith);

        if (typeof slotIndex === 'number' && typeof slotToSwitchWithIndex === 'number') {
            this.slotAssignments[slotIndex] = slotToSwitchWith;
            this.slotAssignments[slotToSwitchWithIndex] = slot;
        }

        this.onSlotChange.emit(this.slotAssignments);

        this.setSlotAnimationIndex(newOrder - 1);
    }

    trackByOrder(_: number, slot: SigningTemplateSlot): number {
        return slot.order;
    }

    private setSlotAnimationIndex(index: number): void {
        this.animateIndices = this.animateIndices.map((_) => false);
        setTimeout(() => {
            this.animateIndices[index] = true;
        });
    }

    private setEmployeeAutocompleteQuery(): void {
        const statuses = ['active'];

        if (this.allowMultiSignDocuments) {
            statuses.push('onboarding');
        }

        this.employeeAutocompleteQuery = Employee.param('company', this.auth?.company?.id)
            .with(['job'])
            .whereIn('status', statuses);
    }

    private async setFeatureFlag(): Promise<void> {
        this.allowMultiSignDocuments = await this.featureService.has(FeatureFlag.onboardingMultiSignDocuments);
    }
}
